import { isNaN } from 'lodash';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';
import { createGlobalStyle, css } from 'styled-components';
import ErrorBoundary from '../../app/src/components/ErrorBoundary';
import Can from '../../app/src/features/permissions/Can';
import {
  isStaging,
  useFlowsSelector,
} from '../../common/src/utils/globalVariables';
import { mq } from '../../common/src/utils/styledComponentsUtils';
import ProductFactoryRouter from '../../sales-conversation/src/Router';
import CompanyScanRouter from '../../company-scan/Router';
import BrokerInfoModalContainer from './components/ui/advisory-flow/components/broker-info/BrokerInfoModalContainer';
import flowsConfig, { defaultFlowsConfig } from './config/flowsConfig';
import { useFlowsRouteParams } from './hooks/useFlowsRouteParams';
import DevToolsView from './pages/dev-tools';
import FlowsProvider from './store/context/FlowsProvider';
import TempLanguageProvider from './store/context/TempLanguageProvider';
import useAuth from '../../app/src/features/authentication/hooks/useAuth';
import SocialRoute from '../../app/src/features/routing/components/SocialRoute';
import { isSocialLoginUrl } from '../../app/src/features/authentication/constants/SocialAuth';
import { askaFlowIds } from './constants/flowIds';
import FlowsConfigRouter, {
  FLOWS_CONFIG_PATH,
} from '@flows/src/FlowsConfigRouter';

const srcUrl = '/flows/:flowId/questions';
const devToolsSrcUrl = '/flows/:flowId';

const Router: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const config = flowsConfig[flowId] || defaultFlowsConfig(flowId);

  return (
    <FlowsProvider AppShell={config?.appShell}>
      <TempLanguageProvider>
        <LouiseV2Styling />
        {/* This switch should contain all modules that use flows */}
        <Switch>
          <Route
            path={['/product/session', '/risk-analysis', '/sales-conversation']}
            component={ProductFactoryRouter}
          />

          <Route
            path={['/company-scan']}
            render={() =>
              isSocialLoginUrl ? (
                <CompanyScanRouter />
              ) : (
                <Can I="use" a="company_scan">
                  <CompanyScanRouter />
                </Can>
              )
            }
          />
          <Route
            path={`${FLOWS_CONFIG_PATH}/:flowId`}
            render={() =>
              isSocialLoginUrl ? (
                <SocialRoute>
                  <FlowsConfigRouter />
                </SocialRoute>
              ) : (
                <FlowsConfigRouter />
              )
            }
          />
          <Route
            path={['/flows/:flowId']}
            render={() =>
              isSocialLoginUrl ? (
                <SocialRoute>
                  <FlowsRouter />
                </SocialRoute>
              ) : (
                <FlowsRouter />
              )
            }
          />
        </Switch>
      </TempLanguageProvider>
    </FlowsProvider>
  );
};

const FlowsRouter: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const flowConfig = flowsConfig[flowId] || defaultFlowsConfig(flowId);
  const { t } = useTranslation();

  const { distribution } = useAuth();

  return (
    <ErrorBoundary>
      <Helmet>
        <title>
          {isSocialLoginUrl
            ? askaFlowIds.includes(flowId)
              ? 'ASKA INSURANCES'
              : distribution.name
            : `${t(flowConfig.title)} | WeGroup`}
        </title>
      </Helmet>

      <BrokerInfoModalContainer />
      <FlowsSwitch />
    </ErrorBoundary>
  );
};

const FlowsSwitch = () => {
  const isLoading = useFlowsSelector((state) => state.flows.isLoading);
  const lastPosition = useFlowsSelector(
    (state) => state.flowsMetaData.lastPosition,
  );
  const initialQuestionnaire = useFlowsSelector(
    (state) => state.flows.initialQuestionnaire,
  );
  const location = useLocation();
  const history = useHistory();
  const { flowId, sessionId, position } = useFlowsRouteParams();

  const flowConfig =
    flowsConfig[flowId || ''] || defaultFlowsConfig(flowId || '');

  useEffect(() => {
    if (position == null && location.pathname.includes('/questions/')) {
      history.replace(
        `/flows/${flowId}/questions/${sessionId}/${lastPosition}`,
      );
    } else if (position === 'NaN' || isNaN(position))
      history.replace(
        `/flows/${flowId}/questions/${sessionId}/${lastPosition}`,
      );
  }, [position]);

  if (
    (position == null && location.pathname.includes('/questions/')) ||
    isLoading ||
    initialQuestionnaire.groups.length === 0
  ) {
    const Loading = flowConfig.loadingView;
    return <Loading />;
  }

  return (
    <Switch>
      <Route path="/flows/:flowId" component={flowConfig.loadingView} exact />

      <Route
        path={[
          `${srcUrl}`,
          `${srcUrl}/:sessionId/`,
          `${srcUrl}/:sessionId/:position`,
          `${srcUrl}/:sessionId/:position/:subView`,
        ]}
        component={flowConfig.visualEnvironment}
        exact
      />

      {isStaging && (
        <Route
          path={[
            `${devToolsSrcUrl}/devtools`,
            `${devToolsSrcUrl}/devtools/:sessionId`,
            `${devToolsSrcUrl}/devtools/:sessionId/:page`,
          ]}
          component={DevToolsView}
          exact
        />
      )}
    </Switch>
  );
};

const LouiseV2Styling = createGlobalStyle`
  html {
    font-size: 62.5% !important;
  }
  
  
  /** Disable special browser effects for mobile */
  body {
    overscroll-behavior-y: contain;
    ${({ theme }) =>
      theme['isImmo'] &&
      css`
        * {
          font-family: Roboto, Circular, Helvetica, sans-serif !important;
        }
      `}
  }

  /** Enable them again on desktop */
  ${mq.laptop} {
    overscroll-behavior-y: auto;

  }
`;

export default Router;
